import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Target, ArrowRight, HandHeart } from "lucide-react";

function ProgressBar({ value, max, label, suffix = "" }) {
  const pct = Math.min(100, max > 0 ? Math.round((value / max) * 100) : 0);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="overline text-[var(--bone)]/60">{label}</div>
        <div className="font-mono text-xs text-[var(--terracotta)]">{pct}%</div>
      </div>
      <div className="flex items-end gap-3 mb-4">
        <div className="font-serif text-6xl md:text-7xl tracking-[-0.04em] leading-none text-[var(--bone)]">
          {value.toLocaleString()}{suffix}
        </div>
        <div className="font-mono text-sm text-[var(--bone)]/60 pb-2">
          of {max.toLocaleString()}{suffix}
        </div>
      </div>
      <div className="w-full h-2 bg-[var(--bone)]/15 overflow-hidden">
        <div
          className="h-full bg-[var(--terracotta)] transition-all duration-1000"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function GoalTracker() {
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    api.get("/goals").then((r) => setGoals(r.data)).catch(() => {});
  }, []);

  if (!goals) return null;

  return (
    <section data-testid="goals-section" id="goals" className="bg-[var(--forest)] text-[var(--bone)] py-24 md:py-32 relative overflow-hidden">
      <div className="grain absolute inset-0 opacity-50" />
      <div aria-hidden className="hidden md:block absolute -left-40 top-10 font-serif text-[35rem] leading-none text-[var(--bone)]/[0.04] select-none">
        →
      </div>

      <div className="container-x relative">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[var(--terracotta)] inline-flex items-center gap-2">
              <Target className="w-3.5 h-3.5" /> Our 2-to-3-year goal
            </div>
            <h2 className="font-serif text-4xl md:text-6xl mt-5 leading-[1.0] tracking-[-0.025em]">
              {goals.schools_target} schools.
              <br/>
              <em className="text-[var(--terracotta)] not-italic">{goals.students_target.toLocaleString()} students.</em>
              <br/>
              By {goals.deadline_year}.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 text-[var(--bone)]/75 text-sm leading-relaxed">
            {goals.note}
            <br/><br/>
            Help us get there. Every donation, every volunteer hour, every shared post moves
            a real classroom closer to existing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 mb-16">
          <ProgressBar
            value={goals.schools_current}
            max={goals.schools_target}
            label="Schools rebuilt / equipped"
          />
          <ProgressBar
            value={goals.students_current}
            max={goals.students_target}
            label="Students reached"
          />
        </div>

        <div className="border border-[var(--bone)]/20 p-6 md:p-8 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div>
            <div className="overline text-[var(--terracotta)]">Be part of the next school</div>
            <div className="font-serif text-2xl md:text-3xl mt-2 tracking-[-0.015em]">
              Donate, volunteer, or partner with us — and we&apos;ll publish the impact you helped create.
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/donate" data-testid="goals-donate-cta" className="btn-primary">
              <HandHeart className="w-4 h-4" /> Donate
            </Link>
            <Link to="/membership" data-testid="goals-membership-cta" className="btn-ghost-light">
              Become a member <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/volunteer" data-testid="goals-volunteer-cta" className="btn-ghost-light">
              Volunteer <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/partner" data-testid="goals-partner-cta" className="btn-ghost-light">
              Partner with us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
